// SidebarLessonTree.tsx
import { Dropdown, IconButton } from '@teo/components';
import {
  PlusFilled,
  Document,
  Chapter,
  Library,
  Play,
  Image,
  ImageFilled,
  Ai,
} from '@teo/components/icons';
import React, { useState, useEffect, useRef } from 'react';
import { LessonTreeEdit } from '@/components/LessonTree/LessonTreeEdit';
import { LibraryModal } from '../LibraryModal/LibraryModal';
import { useTranslation } from 'react-i18next';
import { axios } from '@/axios';
import { config } from '@/config';
import { uploadLesson, uploadImage, uploadFile } from '@/query/documents';
import { newUid, uploadFile as uploadFileSelect } from '@/util';
import { uploadVideo } from '@/query/vimeo';
import Hint from '../Hints/Hints';
import { VideoUploadModal } from '../VideoUploadModal/VideoUploadModal';
import { VideoCameraModal } from '@/components/CameraModal/VideoCameraModal';
import { encode } from '@/base64';
import { Video } from '@/components/Froala/widgets/ChatGPT/Icon/index';
import { AiDocumentJobModal } from '@/components/AiDocumentJobModal';

type TreeNode = any; // refine if you have a concrete type
type LessonType = any;
type FolderType = any;
type AssetType = any;
type HintType = any;

type SidebarLessonTreeProps = {
  authUser?: {
    id?: number | string | null;
    role?: number | null;
    useTeoAiCourseAndLessonGeneration?: boolean | null;
  } | null;
  isDesctop: boolean;
  setMobileSidebar: (open: boolean) => void;
  setActiveNode: (node: TreeNode | null) => void;
  setLesQueryParams: (params: any) => void;
  activeNode: TreeNode | null;
  coursecontentId: string;
  courseLang?: string | null;
  //setPrevTree: React.Dispatch<React.SetStateAction<TreeNode[]>>;
  setPrevTree: any;
  addLesson: LessonType | null;
  setAddLesson: (lesson: LessonType | null) => void;
  addFolder: FolderType | null;
  setAddFolder: (folder: FolderType | null) => void;
  isRefreshPage: boolean;
  setTree: (nodes: TreeNode[]) => void;
  tree: TreeNode[];
  prevTree: TreeNode[];
  handleOpenLibrary: (arg1: any, arg2: any) => void;
  setDirectories: (dirs: any[]) => void;
  directories: any[];
  setLessons: (lessons: any[]) => void;
  lessons: any[];
  setUpdateLesInfo: (v: any) => void;
  setLoadVersion: (v: boolean) => void;
  loadVersion: boolean;
  isFroalaLoaded: boolean | null;
  hints?: HintType | null;
  newPage: string | number;
  publicationType: string | undefined;
  setLoadeContent: (v: boolean) => void;
  loadeContent?: boolean;
};

type ParsedUpload = {
  uid: string;
  base: string;
  statusUrl: string;
  stepsUrl: string;
};

function parseUploadUrl(uploadUrl: string): ParsedUpload {
  // ex: https://.../instruction_uploads/<uid>/camera.webm
  const url = new URL(uploadUrl);
  const parts = url.pathname.split('/').filter(Boolean);
  const filename = parts.pop() || ''; // "camera.webm"
  const uid = parts.pop() || ''; // "<uid>"
  const base = filename.replace(/\.[^.]+$/, ''); // "camera"
  const cdn = `${url.protocol}//${url.host}`;

  const statusUrl = `${cdn}/instruction_transcripts/${uid}/${base}.status.json`;
  const stepsUrl = `${cdn}/instruction_transcripts/${uid}/${base}.steps.json`;
  return { uid, base, statusUrl, stepsUrl };
}

function normalizeCourseLang(lang: any): string | null {
  const value = String(lang || '').trim();

  if (!value) return null;

  // Backend/course records usually store short ISO-like values (nl, en, fr, es).
  // If a regional value sneaks in (nl-BE / nl_BE), send the base language.
  if (/^en[-_]gb$/i.test(value)) return 'en-GB';
  if (/^en[-_]us$/i.test(value)) return 'en-US';
  return value.split(/[-_]/)[0].toLowerCase();
}

function getFirstRecord(value: any): any {
  const data = value?.data ?? value;

  if (Array.isArray(data)) return data[0] || null;
  if (Array.isArray(data?.rows)) return data.rows[0] || null;
  if (Array.isArray(data?.items)) return data.items[0] || null;
  if (Array.isArray(data?.results)) return data.results[0] || null;

  return data || null;
}

function getCourseLangFromRecord(record: any): string | null {
  const data = getFirstRecord(record);

  const candidates = [
    data?.courseLang,
    data?.course_lang,
    data?.coursecontentLang,
    data?.courseContentLang,
    data?.publicationLang,
    data?.publicationLanguage,
    data?.lang,
    data?.language,
    data?.course?.lang,
    data?.course?.language,
    data?.coursecontent?.lang,
    data?.coursecontent?.language,
    data?.courseContent?.lang,
    data?.courseContent?.language,
    data?.publication?.lang,
    data?.publication?.language,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeCourseLang(candidate);
    if (normalized) return normalized;
  }

  return null;
}

function getCourseLangFromLoadedData({
  courseLang,
  activeNode,
  lessons,
  directories,
}: {
  courseLang?: string | null;
  activeNode?: any;
  lessons?: any[];
  directories?: any[];
}): string | null {
  const candidates = [
    courseLang,
    activeNode?.data?.courseLang,
    activeNode?.data?.course_lang,
    activeNode?.data?.coursecontentLang,
    activeNode?.data?.courseContentLang,
    activeNode?.data?.publicationLang,
    activeNode?.data?.publicationLanguage,
    activeNode?.data?.course?.lang,
    activeNode?.data?.course?.language,
    activeNode?.data?.coursecontent?.lang,
    activeNode?.data?.coursecontent?.language,
    activeNode?.data?.courseContent?.lang,
    activeNode?.data?.courseContent?.language,
    lessons?.[0]?.courseLang,
    lessons?.[0]?.course_lang,
    lessons?.[0]?.coursecontentLang,
    lessons?.[0]?.courseContentLang,
    lessons?.[0]?.publicationLang,
    lessons?.[0]?.publicationLanguage,
    directories?.[0]?.courseLang,
    directories?.[0]?.course_lang,
    directories?.[0]?.coursecontentLang,
    directories?.[0]?.courseContentLang,
    directories?.[0]?.publicationLang,
    directories?.[0]?.publicationLanguage,
  ];

  for (const candidate of candidates) {
    const normalized = normalizeCourseLang(candidate);
    if (normalized) return normalized;
  }

  return null;
}

async function fetchCourseLang(
  coursecontentId: number
): Promise<string | null> {
  const requests: Array<{ url: string; params?: Record<string, any> }> = [
    { url: `${config.backend}/coursecontents/${coursecontentId}` },
    { url: `${config.backend}/coursecontentdetails/${coursecontentId}` },
    { url: `${config.backend}/coursedetails/${coursecontentId}` },
    {
      url: `${config.backend}/coursecontentdetails`,
      params: { id: coursecontentId },
    },
    { url: `${config.backend}/coursedetails`, params: { id: coursecontentId } },
  ];

  for (const request of requests) {
    try {
      const response = await axios.get(request.url, {
        params: request.params,
      });
      const lang = getCourseLangFromRecord(response.data);

      if (lang) return lang;
    } catch {
      // Older deployments expose course data through different REST resources.
      // Keep trying the next known endpoint.
    }
  }

  return null;
}

export const SidebarLessonTree: React.FC<SidebarLessonTreeProps> = ({
  authUser,
  isDesctop,
  setMobileSidebar,
  setActiveNode,
  setLesQueryParams,
  activeNode,
  coursecontentId,
  courseLang: courseLangProp,
  setPrevTree,
  addLesson,
  setAddLesson,
  addFolder,
  setAddFolder,
  isRefreshPage,
  setTree,
  tree,
  prevTree,
  handleOpenLibrary,
  setDirectories,
  directories,
  setLessons,
  lessons,
  setUpdateLesInfo,
  setLoadVersion,
  loadVersion,
  isFroalaLoaded,
  hints,
  newPage,
  publicationType,
  setLoadeContent,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [newLesson, setNewLesson] = useState<any>();
  const [newFolder, setNewFolder] = useState<any>();
  const [newAsset, setNewAsset] = useState<any>();
  const [newActiveNode, setNewActiveNode] = useState<number | null>(null);

  const [isOpenImageLibrary, setIsOpenImageLibrary] = useState(false);
  const [isOpenVideoModal, setIsOpenVideoModal] = useState(false);
  const [isAddButtonBottom, setIsAddButtonBottom] = useState(false);
  const [isOpenRecord, setIsOpenRecord] = useState(false);
  const [isOpenAiLessonModal, setIsOpenAiLessonModal] = useState(false);
  const [fetchedCourseLang, setFetchedCourseLang] = useState<string | null>(
    null
  );

  const pendingAiLessonUsesIdRef = useRef<number | null>(null);

  const courseLangFromLoadedData = getCourseLangFromLoadedData({
    courseLang: courseLangProp,
    activeNode,
    lessons,
    directories,
  });
  const aiLessonCourseLang = courseLangFromLoadedData || fetchedCourseLang;

  useEffect(() => {
    setFetchedCourseLang(null);
  }, [coursecontentId]);

  useEffect(() => {
    const parsedCoursecontentId = parseInt(coursecontentId, 10);

    if (
      !parsedCoursecontentId ||
      courseLangFromLoadedData ||
      fetchedCourseLang
    ) {
      return undefined;
    }

    let cancelled = false;

    fetchCourseLang(parsedCoursecontentId)
      .then((lang) => {
        if (!cancelled && lang) {
          setFetchedCourseLang(lang);
        }
      })
      .catch((err) => {
        console.warn(
          'Could not resolve course language for AI lesson job',
          err
        );
      });

    return () => {
      cancelled = true;
    };
  }, [coursecontentId, courseLangFromLoadedData, fetchedCourseLang]);

  const sleep = (ms: number) =>
    new Promise((resolve) => {
      window.setTimeout(resolve, ms);
    });

  const getNodeUsesId = (node: any) =>
    Number(node?.id || node?.data?.usesId || node?.data?.id || 0);

  const findTreeNodeByUsesId = (nodes: any[] = [], usesId: number): any => {
    for (const node of nodes || []) {
      if (getNodeUsesId(node) === usesId) return node;

      const childMatch = findTreeNodeByUsesId(node?.children || [], usesId);
      if (childMatch) return childMatch;
    }

    return null;
  };

  const getNextTopLevelYindex = () => {
    const sourceTree =
      Array.isArray(prevTree) && prevTree.length ? prevTree : tree || [];

    const topTree = sourceTree.filter(
      (item: any) => Number(item?.parent || 0) === 0
    );

    if (!topTree.length) return 0;

    return (
      topTree.reduce((max: number, item: any) => {
        const yindex = Number(item?.data?.yindex ?? item?.yindex ?? 0);
        return Number.isFinite(yindex) && yindex > max ? yindex : max;
      }, Number(topTree[0]?.data?.yindex ?? topTree[0]?.yindex ?? 0)) + 1
    );
  };

  const buildActiveNodeFromLessonRow = (lesson: any, usesId: number) => {
    if (!lesson) return null;

    const nodeId = Number(lesson?.usesId || lesson?.id || usesId);
    const lessonId = Number(lesson?.lessonId || lesson?.id || 0);
    const name =
      lesson?.name ||
      lesson?.lessonName ||
      lesson?.title ||
      t('pages.create_edit_course.new_lesson_button') ||
      'AI generated lesson';

    return {
      id: nodeId,
      text: name,
      parent: lesson?.parent ?? 0,
      data: {
        ...lesson,
        id: nodeId,
        usesId: nodeId,
        lessonId,
        name,
      },
    };
  };

  const fetchCourseIndex = async (targetUsesId?: number | null) => {
    const parsedCoursecontentId = parseInt(coursecontentId, 10);

    if (!parsedCoursecontentId) return null;

    const params = { coursecontentId: parsedCoursecontentId };

    const [lessonResult, directoryResult] = await Promise.allSettled([
      axios.get(`${config.backend}/usedlessonassets`, { params }),
      axios.get(`${config.backend}/directoriedetails`, { params }),
    ]);

    if (lessonResult.status === 'rejected') {
      console.error('Failed to refresh AI lesson index', lessonResult.reason);
      throw lessonResult.reason;
    }

    const nextLessons = Array.isArray(lessonResult.value?.data)
      ? lessonResult.value.data
      : [];

    const nextDirectories =
      directoryResult.status === 'fulfilled' &&
      Array.isArray(directoryResult.value?.data)
        ? directoryResult.value.data
        : directories;

    if (directoryResult.status === 'rejected') {
      console.warn(
        'Failed to refresh directories after AI lesson creation',
        directoryResult.reason
      );
    }

    setLessons(nextLessons);
    setDirectories(nextDirectories || []);

    const matchedLesson = targetUsesId
      ? nextLessons.find(
          (lesson: any) =>
            Number(lesson?.usesId || lesson?.id || 0) === targetUsesId
        )
      : null;

    return {
      lessons: nextLessons,
      directories: nextDirectories,
      matchedLesson,
    };
  };

  const refreshCourseIndexUntilLessonExists = async (usesId: number) => {
    let latest: any = null;

    for (let attempt = 0; attempt < 6; attempt += 1) {
      latest = await fetchCourseIndex(usesId);

      if (latest?.matchedLesson?.getData) return latest;

      if (latest?.matchedLesson) return latest;

      await sleep(350);
    }

    return latest;
  };

  const triggerLessonTreeRefresh = () => {
    // setLoadVersion(true) is a no-op when it is already true, so toggle it.
    setLoadVersion(false);
    window.setTimeout(() => setLoadVersion(true), 0);
  };

  const unwrapAiLessonResult = (result: any) => {
    return (
      result?.result || result?.data?.result || result?.data || result || {}
    );
  };

  const getAiCreatedLessonIds = (result: any) => {
    const data = unwrapAiLessonResult(result);

    return {
      data,
      lessonId: Number(
        data?.lessonId ||
          data?.lesson?.id ||
          data?.use?.lessonId ||
          data?.usedLesson?.lessonId ||
          0
      ),
      usesId: Number(
        data?.usesId ||
          data?.useId ||
          data?.use?.id ||
          data?.usedLesson?.id ||
          0
      ),
    };
  };

  const refreshIndexAndSwitchToAiLesson = async (result: any) => {
    const { lessonId, usesId } = getAiCreatedLessonIds(result);

    if (!lessonId || !usesId) {
      console.warn('AI lesson job completed without lessonId/usesId', result);
      triggerLessonTreeRefresh();
      return null;
    }

    pendingAiLessonUsesIdRef.current = usesId;
    setNewActiveNode(usesId);

    const existingCanonicalNode = findTreeNodeByUsesId(tree || [], usesId);

    if (existingCanonicalNode?.data?.getData) {
      pendingAiLessonUsesIdRef.current = null;
      setActiveNode(existingCanonicalNode);
      window.scrollTo(0, 0);
      return { lessonId, usesId };
    }

    try {
      const refreshed = await refreshCourseIndexUntilLessonExists(usesId);
      const refreshedLesson = refreshed?.matchedLesson;

      if (refreshedLesson?.getData) {
        const refreshedNode = buildActiveNodeFromLessonRow(
          refreshedLesson,
          usesId
        );

        if (refreshedNode) {
          pendingAiLessonUsesIdRef.current = null;
          setActiveNode(refreshedNode);
          window.scrollTo(0, 0);
        }
      }

      // Also let LessonTreeEdit rebuild its canonical tree from the freshly updated
      // lessons/directories props. This is a fallback for any extra tree metadata it adds.
      triggerLessonTreeRefresh();
    } catch (err) {
      console.error(
        'Failed to refresh course index after AI lesson creation',
        err
      );
      triggerLessonTreeRefresh();
    }

    return { lessonId, usesId };
  };

  const goToAiCreatedLesson = async (result: any) => {
    await refreshIndexAndSwitchToAiLesson(result);
    setMobileSidebar(false);
  };

  useEffect(() => {
    function handleLessonRenamed(ev: Event) {
      const custom = ev as CustomEvent<{ lessonId?: number; name?: string }>;
      const detail = custom.detail || {};
      const { lessonId, name } = detail;

      if (!lessonId || !name) return;

      // ----- Update lessons -----
      if (Array.isArray(lessons) && typeof setLessons === 'function') {
        const nextLessons = lessons.map((item: any) =>
          item?.lessonId === lessonId ? { ...item, name } : item
        );
        setLessons(nextLessons);
      }

      // ----- Update tree -----
      if (Array.isArray(tree) && typeof setTree === 'function') {
        const nextTree = tree.map((node: any) => {
          if (node?.data?.lessonId === lessonId) {
            return {
              ...node,
              text: name,
              data: {
                ...node.data,
                name,
              },
            };
          }
          return node;
        });
        setTree(nextTree);
      }
    }

    window.addEventListener('teo:lessonRenamed', handleLessonRenamed);
    return () =>
      window.removeEventListener('teo:lessonRenamed', handleLessonRenamed);
  }, [lessons, tree, setLessons, setTree]);

  const checkPositionAddButton = () => {
    const blockTree = document.getElementById('course-lessons');
    const contentTree = document.getElementById('course-lessons-list');
    if (blockTree && contentTree) {
      const blockTreeHeight = blockTree.offsetHeight;
      const contentTreeHeight = contentTree.offsetHeight;
      if (blockTreeHeight > contentTreeHeight + 220) {
        setIsAddButtonBottom(false);
      } else {
        setIsAddButtonBottom(true);
      }
    }
  };

  useEffect(() => {
    if (newActiveNode && tree) {
      const newNode = findTreeNodeByUsesId(tree || [], newActiveNode);

      // The backend-created AI lesson may arrive before the refreshed tree does.
      // Only switch when the canonical backend-built lesson node is present,
      // because LessonEdit needs node.data.getData to finish loading.
      if (newNode) {
        const isLessonNode = !!newNode?.data?.lessonId;
        const hasLessonDataUrl = !!newNode?.data?.getData;
        const isAssetNode = !!newNode?.data?.assetPath;

        if (!isLessonNode || hasLessonDataUrl || isAssetNode) {
          pendingAiLessonUsesIdRef.current = null;
          setActiveNode(newNode);
          window.scrollTo(0, 0);
        }
      }
    }
    checkPositionAddButton();
  }, [tree, newActiveNode, setActiveNode]);

  const handleStateLesson = async (state: any): Promise<number> => {
    const content = `<div style="position:relative" contenteditable=false class="widget-placeholder fr-deletable" id="${
      state.uid
    }"><div data-open="SOW" data-state="${encode(
      JSON.stringify(state)
    )}"><div data-closed="EOW"></div></div></div>`;

    const title =
      state.title ||
      t('pages.create_edit_course.new_lesson_button') ||
      'New lesson';

    return handleNewLesson(title, content, 'instruction');
  };

  const handleNewLesson = async (
    name: string = t('pages.create_edit_course.new_lesson_button'),
    content = '',
    lessonType = 'theory'
  ): Promise<number> => {
    try {
      const { data: lesson } = await axios.post(`${config.backend}/lessons`, {
        name: name,
        canEdit: true,
        description: '',
        lessonType: lessonType,
      });

      // Store the content alongside the lesson so the effect can upload it
      setNewLesson({ ...lesson, _pendingContent: content });

      return lesson.id as number;
    } catch (err) {
      console.error('🔥 Failed to create lesson:', err);
      throw err;
    }
  };

  const createLesson = async () => {
    const result = await axios.post(`${config.backend}/uses`, {
      canEdit: true,
      coursecontentId: parseInt(coursecontentId, 10),
      lessonId: newLesson?.id,
    });

    const createLessonId = result?.data?.lessonId as number | undefined;
    const createExtraInfoUsesId = result?.data?.id as number | undefined;

    if (createLessonId && createExtraInfoUsesId != null) {
      let newYindex: number;
      const topTree = prevTree.filter((item: any) => item?.parent === 0);
      if (topTree?.length) {
        newYindex =
          topTree?.reduce((max: number, obj: any) => {
            return obj?.data?.yindex > max ? obj?.data?.yindex : max;
          }, topTree[0]?.data?.yindex) + 1;
      } else {
        newYindex = 0;
      }
      axios.put(`${config.backend}/use/${result?.data?.id}`, {
        coursecontentId: parseInt(coursecontentId, 10),
        lessonId: createLessonId,
        path: `/${createLessonId}`,
        yindex: newYindex,
      });
      setAddLesson({
        ...newLesson,
        canEdit: true,
        coursecontentId: parseInt(coursecontentId, 10),
        numbered: true,
        lessonId: createLessonId,
        path: `/${createLessonId}`,
        usesId: createExtraInfoUsesId,
        yindex: newYindex,
      });
      setNewActiveNode(createExtraInfoUsesId);
      setNewLesson(null);
    }
  };

  useEffect(() => {
    if (!newLesson?.id) return;

    const content = newLesson._pendingContent ?? '';

    uploadLesson(newLesson.postData, content)
      .then(() => {
        createLesson();
      })
      .catch((err: unknown) => {
        console.error('❌ Upload failed for lesson', newLesson.id, err);
      });
  }, [newLesson]);

  const createFolder = async () => {
    const data = await axios.post(`${config.backend}/directories`, {
      coursecontentId: parseInt(coursecontentId, 10),
      name: t('pages.create_edit_course.new_chapter_button'),
    });
    setNewFolder(data?.data);
  };

  useEffect(() => {
    if (newFolder?.id) {
      const newPath = `/${newUid(20)}`;
      let newYindex: number;
      const topTree = prevTree.filter((item: any) => item?.parent === 0);
      if (topTree?.length) {
        newYindex =
          topTree?.reduce((max: number, obj: any) => {
            return obj?.data?.yindex > max ? obj?.data?.yindex : max;
          }, topTree[0]?.data?.yindex) + 1;
      } else {
        newYindex = 0;
      }
      axios.put(`${config.backend}/directory/${newFolder?.id}`, {
        coursecontentId: parseInt(coursecontentId, 10),
        id: newFolder?.id,
        name: t('pages.create_edit_course.new_folder'),
        path: newPath,
        yindex: newYindex,
      });
      setAddFolder({
        ...newFolder,
        coursecontentId: parseInt(coursecontentId, 10),
        id: newFolder?.id,
        numbered: true,
        name: t('pages.create_edit_course.new_folder'),
        path: newPath,
        yindex: newYindex,
      });
      setNewActiveNode(newFolder?.id as number);
      setNewFolder(null);
    }
  }, [newFolder?.id]);

  const handleUploadImage = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const img = event.target.files?.[0];
    if (!img) return;
    const size = 900;
    const data = await uploadImage(img, size, size);

    const dataImage = await axios.post(`${config.backend}/assets`, {
      assetName: t('pages.create_edit_course.new_asset'),
      assetType: 'image',
      assetPath: data,
    });
    setNewAsset(dataImage?.data);
  };

  const handleUploadImageLibrary = async (image: string) => {
    const res = await fetch(image);
    const blob = await res.blob();
    const mime = blob.type;
    const ext = mime.slice(mime.lastIndexOf('/') + 1, mime.length);
    const img = new File([blob], `${blob.size}.${ext}`, {
      type: mime,
    });
    const size = 900;
    const data = await uploadImage(img, size, size);

    const dataImage = await axios.post(`${config.backend}/assets`, {
      assetName: t('pages.create_edit_course.new_asset'),
      assetType: 'image',
      assetPath: data,
    });
    setNewAsset(dataImage?.data);
  };

  const handleUseImageLibrary = async (imageUrl: any) => {
    const dataImage = await axios.post(`${config.backend}/assets`, {
      assetName: t('pages.create_edit_course.new_asset'),
      assetType: 'image',
      assetPath: imageUrl,
    });
    setNewAsset(dataImage?.data);
  };

  useEffect(() => {
    if (newAsset) {
      createImageInfo();
    }
  }, [newAsset]);

  const createImageInfo = async () => {
    const result = await axios.post(`${config.backend}/uses`, {
      coursecontentId: parseInt(coursecontentId, 10),
      assetId: newAsset?.id,
    });

    const createAssetId = result?.data?.assetId as number | undefined;
    const createAssetUsesId = result?.data?.id as number | undefined;

    if (createAssetId && createAssetUsesId != null) {
      let newYindex: number;
      const topTree = prevTree.filter((item: any) => item?.parent === 0);
      if (topTree?.length) {
        newYindex =
          topTree?.reduce((max: number, obj: any) => {
            return obj?.data?.yindex > max ? obj?.data?.yindex : max;
          }, topTree[0]?.data?.yindex) + 1;
      } else {
        newYindex = 0;
      }
      axios.put(`${config.backend}/use/${result?.data?.id}`, {
        assetId: createAssetId,
        canEdit: true,
        coursecontentId: parseInt(coursecontentId, 10),
        onlyTheory: true,
        path: `/${createAssetId}`,
        yindex: newYindex,
      });
      setAddLesson({
        ...newAsset,
        canEdit: true,
        coursecontentId: parseInt(coursecontentId, 10),
        onlyTheory: true,
        numbered: true,
        assetId: createAssetId,
        path: `/${createAssetId}`,
        usesId: createAssetUsesId,
        yindex: newYindex,
      });
      setNewActiveNode(createAssetUsesId);
      setNewAsset(null);
    }
  };

  useEffect(() => {
    if (isOpenImageLibrary) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [isOpenImageLibrary]);

  function startInstruction() {
    const widgetState = {
      uid: newUid(10),
      type: 'Instruction',
      titleWidget: 'Instruction',
      noSubmit: true,
      url: null,
      title: t('pages.create_edit_course.video_instruction.new_instructional'),
      statusUrl: null,
      stepsUrl: null,
      steps: [],
    };

    handleStateLesson(widgetState);
  }

  function startInstructionUpload(file: File) {
    if (!file) return;

    (async () => {
      try {
        // Step 1: reserve upload & get final public URL,
        // but DO NOT wait for the binary upload (fire-and-forget)
        const uploadUrl: string = await uploadFile(
          '/instruction_uploads',
          file,
          {
            fireAndForget: true,
          }
        );

        // Derive uid + status/steps URLs from the final path
        const { uid, base, statusUrl, stepsUrl } = parseUploadUrl(uploadUrl);

        // Initial widget state: no steps yet, but we know where status & steps will appear
        const widgetState = {
          uid,
          type: 'Instruction',
          titleWidget: 'Instruction',
          noSubmit: true,
          url: uploadUrl,
          title: t('pages.create_edit_course.new_instruction_title'),
          statusUrl,
          stepsUrl,
          steps: [],
        };

        // Create the lesson immediately with the widget in it
        await handleStateLesson(widgetState);
      } catch (err) {
        console.error('Failed to upload instruction video', err);
      }
    })();
  }

  return (
    <div className="min-h-[calc(100vh_-_190px)]">
      <div
        className={`${
          hints?.id == 'hint_40' && 'max-h-[440px] overflow-hidden'
        }`}
      >
        <LessonTreeEdit
          onRefresh={async () => {
            if (activeNode?.data?.lessonId && authUser?.id) {
              await axios.delete(
                config.backend + '/lessonlock/' + activeNode.data.lessonId
              );
              window.location.reload();
            } else {
              setLoadVersion(true);
            }
          }}
          coursecontentId={coursecontentId}
          activeNode={activeNode}
          onClickNode={(ev, node) => {
            window.scrollTo(0, 0);
            setActiveNode(node);
            setMobileSidebar(false);
          }}
          setPrevTree={setPrevTree}
          addLesson={addLesson}
          setAddLesson={setAddLesson}
          addFolder={addFolder}
          setAddFolder={setAddFolder}
          isRefreshPage={isRefreshPage}
          setTree={setTree}
          tree={tree}
          handleOpenLibrary={handleOpenLibrary}
          setDirectories={setDirectories}
          directories={directories}
          setLessons={setLessons}
          lessons={lessons}
          setUpdateLesInfo={setUpdateLesInfo}
          setLoadVersion={setLoadVersion}
          loadVersion={loadVersion}
          setNewActiveNode={setNewActiveNode}
          newActiveNode={newActiveNode}
          isFroalaLoaded={isFroalaLoaded}
          setLoadeContent={setLoadeContent}
          setActiveNode={setActiveNode}
        />
      </div>
      <div
        className={`relative mt-1 ml-3 flex w-min items-center sm:w-auto ${
          hints?.id == 'hint_40' && 'active-hint-parent'
        }`}
      >
        <Hint
          key={`hint_40_${newPage}`}
          id={'hint_40'}
          leftDots={'-left-2 top-0.5'}
          title={t('hint.title_40')}
          order={40}
          style={{
            left: 'calc(100% + 20px)',
            bottom: '-100px',
          }}
        />
        <Dropdown>
          <Dropdown.Trigger>
            <IconButton
              Icon={PlusFilled}
              size="sm"
              variant="secondary"
              className={`mr-3 bg-[#eaeaed] text-[#231f20] ${
                hints?.id == 'hint_40' && 'active-hint-button'
              }`}
            />
          </Dropdown.Trigger>
          <Dropdown.Content
            style={
              !isDesctop
                ? {
                    left: '35px',
                    bottom: '50%',
                    top: 'auto',
                    transform:
                      tree?.length > 5
                        ? isAddButtonBottom
                          ? 'translateY(10%)'
                          : 'translateY(50%)'
                        : tree?.length > 0
                        ? 'translateY(80%)'
                        : 'translateY(94%)',
                  }
                : {
                    left: '25px',
                    bottom: '50%',
                    top: 'auto',
                    transform:
                      tree?.length > 5
                        ? isAddButtonBottom
                          ? 'translateY(10%)'
                          : 'translateY(50%)'
                        : tree?.length > 0
                        ? 'translateY(80%)'
                        : 'translateY(94%)',
                  }
            }
          >
            <Dropdown.Actions>
              <Dropdown.Button
                onClick={() => {
                  handleNewLesson();
                }}
              >
                <Document className="m-2 mr-4 h-5 w-5 text-grey-07" />
                <p>{t('pages.create_edit_course.new_lesson_button')}</p>
              </Dropdown.Button>

              <Dropdown.Button onClick={() => createFolder()}>
                <Chapter className="m-2 mr-4 h-5 w-5 text-grey-07" />
                <p>{t('pages.create_edit_course.new_chapter_button')}</p>
              </Dropdown.Button>

              <Dropdown.Button
                onClick={() => {
                  setIsOpenImageLibrary(true);
                }}
              >
                <Image className="m-2 mr-4 h-5 w-5 text-grey-07" />
                <p>{t('extra_info.upload_img')}</p>
              </Dropdown.Button>

              <Dropdown.Button onClick={() => setIsOpenVideoModal(true)}>
                <Play className="m-2 mr-4 h-5 w-5 text-grey-07" />
                <p>{t('extra_info.upload_video')}</p>
              </Dropdown.Button>

              <Dropdown.Button
                onClick={() => {
                  handleOpenLibrary(null, null);
                }}
              >
                <Library className="m-2 mr-4 h-5 w-5 text-tint-blue" />
                <p>{t('pages.create_edit_course.from_library_button')}</p>
              </Dropdown.Button>

              {authUser?.useTeoAiCourseAndLessonGeneration && (
                <Dropdown.Button onClick={() => setIsOpenAiLessonModal(true)}>
                  <Ai className="m-2 mr-4 h-5 w-5 text-tint-blue" />
                  <p>{t('pages.ai_document_job_modal.create_lesson_title')}</p>
                </Dropdown.Button>
              )}

              {/* <Dropdown.Button onClick={() => setIsOpenRecord(true)}>
                <IconButton
                  Icon={Play}
                  variant="ghost"
                  className="mr-2 text-[#231f20]"
                />
                <p>{t('pages.create_edit_course.record_instruction')}</p>
              </Dropdown.Button> */}

              {/* <Dropdown.Button
                onClick={async () => {
                  const files = (await uploadFileSelect(
                    false,
                    'video/*'
                  )) as File[];
                  const file = files?.[0];
                  if (!file) return;
                  startInstructionUpload(file);
                }}
              >
                <IconButton
                  Icon={Upload}
                  variant="ghost"
                  className="mr-2 text-[#231f20]"
                />
                <p>{t('pages.create_edit_course.upload_instruction')}</p>
              </Dropdown.Button> */}
              <Dropdown.Button
                onClick={() => {
                  startInstruction();
                }}
              >
                <Video className={`m-2 mr-4 h-5 w-5 text-grey-07`} />

                <p>
                  {t(
                    'pages.create_edit_course.video_instruction.instructional_video'
                  )}
                </p>
              </Dropdown.Button>
            </Dropdown.Actions>
          </Dropdown.Content>
        </Dropdown>

        <p className="w-52 text-xs font-normal text-[#919193] sm:w-auto">
          {t('pages.create_edit_course.start_adding_text')}
        </p>

        <input
          type="file"
          ref={fileInputRef}
          onChange={handleUploadImage}
          accept="image/*"
          style={{ display: 'none' }}
          multiple={false}
        />
      </div>

      {authUser?.useTeoAiCourseAndLessonGeneration && (
        <AiDocumentJobModal
          open={isOpenAiLessonModal}
          mode="lesson"
          coursecontentId={coursecontentId}
          extraPayload={{
            ...(aiLessonCourseLang ? { lang: aiLessonCourseLang } : {}),
            parentPath: '',
            yindex: getNextTopLevelYindex(),
          }}
          onClose={() => setIsOpenAiLessonModal(false)}
          onCompleted={goToAiCreatedLesson}
          title={t('pages.ai_document_job_modal.create_lesson_title')}
          description={t(
            'pages.ai_document_job_modal.create_lesson_description'
          )}
          promptPlaceholder={t(
            'pages.ai_document_job_modal.lesson_prompt_placeholder'
          )}
          startLabel={t('pages.ai_document_job_modal.start_lesson_job')}
          completedActionLabel={t('pages.ai_document_job_modal.go_to_lesson')}
        />
      )}

      {isOpenImageLibrary && (
        <LibraryModal
          setIsOpenLibraryModal={setIsOpenImageLibrary}
          isOpenLibraryModal={isOpenImageLibrary}
          lessonPage={false}
          setUploadFile={handleUploadImageLibrary}
          onSelectImage={handleUseImageLibrary}
        />
      )}

      {isOpenVideoModal && (
        <VideoUploadModal
          setIsOpenVideoModal={setIsOpenVideoModal}
          isOpenVideoModal={isOpenVideoModal}
          setNewAsset={setNewAsset}
        />
      )}

      {isOpenRecord && (
        <VideoCameraModal
          forceSound={true}
          setIsOpenVideoModal={setIsOpenRecord}
          isOpenVideoModal={isOpenRecord}
          setUploadFile={(resultFn: (args: any[]) => any[]) => {
            const result = resultFn([]);
            if (!result || !result[0]) return;
            const file = new File(
              result.map((x: any) => x.blob),
              `camera.${result[0].mimeType}`,
              { type: result[0].type }
            );
            startInstructionUpload(file);
          }}
        />
      )}
    </div>
  );
};
